import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { toast } from 'sonner';
import { Phone, Mail, Calendar, FileText, Activity, ExternalLink } from 'lucide-react';

const ACTIVITY_TYPES = [
  { value: 'call', label: 'Anruf', icon: Phone, color: 'bg-green-500' },
  { value: 'email', label: 'E-Mail', icon: Mail, color: 'bg-blue-500' },
  { value: 'meeting', label: 'Meeting', icon: Calendar, color: 'bg-purple-500' },
  { value: 'note', label: 'Notiz', icon: FileText, color: 'bg-gray-400' }
];

export const Activities = () => {
  const navigate = useNavigate();
  const { get } = useApi();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [leadsMap, setLeadsMap] = useState({});

  const loadActivities = useCallback(async () => {
    setLoading(true);
    try {
      let endpoint = '/activities';
      if (typeFilter && typeFilter !== 'all') {
        endpoint += `?typ=${typeFilter}`;
      }
      
      const [activitiesData, leadsData] = await Promise.all([
        get(endpoint),
        get('/leads')
      ]);
      
      // Create leads map for quick lookup
      const map = {};
      leadsData.forEach(lead => {
        map[lead.id] = lead;
      });
      setLeadsMap(map);
      setActivities(activitiesData);
    } catch (error) {
      toast.error('Fehler beim Laden der Aktivitäten');
    } finally {
      setLoading(false);
    }
  }, [get, typeFilter]);

  useEffect(() => {
    loadActivities();
  }, [loadActivities]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', { 
      day: '2-digit', 
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActivityType = (typ) => {
    return ACTIVITY_TYPES.find(t => t.value === typ) || ACTIVITY_TYPES[3];
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-48"></div>
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-24 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="activities-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-3xl font-bold text-gray-900">Aktivitäten</h1>
        <div className="flex items-center gap-4">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[180px]" data-testid="activity-type-filter">
              <Activity className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Alle Typen" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Typen</SelectItem>
              {ACTIVITY_TYPES.map(t => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card data-testid="activities-list-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Aktivitäten-Log
            <Badge variant="secondary" className="ml-2">
              {activities.length}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {activities.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Keine Aktivitäten gefunden</p>
            </div>
          ) : (
            <div className="space-y-4">
              {activities.map((activity) => {
                const activityType = getActivityType(activity.typ);
                const lead = leadsMap[activity.lead_id];
                const ActivityIcon = activityType.icon;

                return (
                  <div 
                    key={activity.id} 
                    className="flex items-start gap-4 p-4 rounded-lg border border-gray-100 hover:border-gray-200 transition-colors"
                    data-testid={`activity-item-${activity.id}`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${activityType.color}`}>
                      <ActivityIcon className="h-5 w-5 text-white" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-gray-900">
                          {activityType.label}
                        </span>
                        <span className="text-gray-400">•</span>
                        {lead && (
                          <Button
                            variant="link"
                            className="p-0 h-auto text-[#E63946] hover:text-[#D62839]"
                            onClick={() => navigate(`/leads/${activity.lead_id}`)}
                          >
                            <span className="font-mono text-xs mr-1">{lead.lead_id}</span>
                            {lead.firma}
                            <ExternalLink className="h-3 w-3 ml-1" />
                          </Button>
                        )}
                      </div>
                      
                      <p className="text-gray-700">{activity.beschreibung}</p>
                      
                      {activity.ergebnis && (
                        <p className="mt-1 text-sm text-gray-600">
                          <span className="font-medium">Ergebnis:</span> {activity.ergebnis}
                        </p>
                      )}
                      
                      {activity.naechster_schritt && (
                        <p className="mt-1 text-sm text-[#E63946]">
                          <span className="font-medium">→</span> {activity.naechster_schritt}
                        </p>
                      )}
                      
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                        <span>{activity.created_by_name}</span>
                        <span>{formatDate(activity.created_at)}</span>
                        {activity.dauer_minuten && (
                          <span>{activity.dauer_minuten} Min.</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
